/**
 * Contract Hub Ask Cenna (SSE) — ``/api/assistant/*``.
 *
 * Uses XMLHttpRequest + ``onprogress`` instead of ``fetch`` + ``ReadableStream``.
 * Office task panes (WKWebView) often buffer or omit fetch body streams, which
 * leaves the UI spinning with no deltas.
 */

import { getStoredAccessToken, readErrorMessage, resolveApiUrl } from "./client";
import { formatContractHubDetail } from "./contractHubApiErrors";

const ASSISTANT_BASE = "/api/assistant";
const BRIDGE_CAPABILITIES_PATH = `${ASSISTANT_BASE}/bridge/capabilities`;
const SESSION_STORAGE_KEY = "word_assistant_session";

export type AssistantChatContext = {
  view: string;
  ticketId?: string;
  contractId?: string;
  partyId?: string;
  playbookId?: string;
  selectedText?: string;
};

export type AssistantStep = {
  name: string;
  input?: Record<string, unknown>;
};

export type AssistantModel = {
  id: string;
  label: string;
  backend: string;
  /** Hub catalog provider (`anthropic` | `openai` | `vertex`). */
  provider?: string;
};

export type AssistantModelsResponse = {
  models: AssistantModel[];
  /** Capabilities → Assistant default model id (bare hub catalog id). */
  default: string;
};

export type AssistantStreamHandlers = {
  onDelta?: (text: string) => void;
  onTool?: (step: AssistantStep) => void;
  onDone?: (text: string) => void;
  onError?: (message: string) => void;
};

export function getStoredAssistantSessionId(): string | null {
  try {
    return localStorage.getItem(SESSION_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function setStoredAssistantSessionId(id: string | null): void {
  try {
    if (id) {
      localStorage.setItem(SESSION_STORAGE_KEY, id);
    } else {
      localStorage.removeItem(SESSION_STORAGE_KEY);
    }
  } catch {
    // ignore
  }
}

export async function createAssistantSession(
  context?: AssistantChatContext | null
): Promise<string> {
  const token = getStoredAccessToken();
  const url = resolveApiUrl(`${ASSISTANT_BASE}/sessions`);
  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ context: context ?? null }),
  });
  if (!res.ok) {
    throw new Error(await readErrorMessage(res));
  }
  const body = (await res.json()) as { id?: string };
  const id = typeof body.id === "string" ? body.id : "";
  if (!id) {
    throw new Error("Assistant session create failed.");
  }
  setStoredAssistantSessionId(id);
  return id;
}

export async function ensureAssistantSession(
  context?: AssistantChatContext | null
): Promise<string> {
  const existing = getStoredAssistantSessionId();
  if (existing) {
    return existing;
  }
  return createAssistantSession(context);
}

/**
 * Map add-in chat catalog ids (`anthropic/claude-opus-4-8`) to Hub Model catalog
 * ids (`claude-opus-4-8`) used by ``/api/assistant/chat``.
 */
export function toAssistantModelId(chatModelId: string | null | undefined): string | null {
  const mid = (chatModelId || "").trim();
  if (!mid) {
    return null;
  }
  if (!mid.includes("/")) {
    return mid;
  }
  const bare = mid.split("/", 1)[1]?.trim();
  return bare || mid;
}

/**
 * Whether the signed-in member may use Ask Cenna (``assistant:use``).
 * Uses the same Jwt-only bridge capabilities endpoint as Apex Ask Cenna.
 */
export async function fetchCanUseAssistant(): Promise<boolean> {
  const token = getStoredAccessToken();
  const url = resolveApiUrl(BRIDGE_CAPABILITIES_PATH);
  const res = await fetch(url, {
    method: "GET",
    headers: {
      Accept: "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!res.ok) {
    throw new Error(await readErrorMessage(res));
  }
  const raw = (await res.json()) as Record<string, unknown>;
  const caps = raw.capabilities;
  if (caps && typeof caps === "object" && !Array.isArray(caps)) {
    return (caps as Record<string, unknown>).assistant === true;
  }
  const permissions = raw.permissions;
  if (Array.isArray(permissions)) {
    return permissions.includes("assistant:use") || permissions.includes("*:*");
  }
  return false;
}

/**
 * Capabilities-enabled Ask Cenna models (GET ``/api/assistant/models``).
 * Same source as Hub web Ask Cenna — respects ``capabilities.chat.models`` + default.
 */
export async function fetchAssistantModels(): Promise<AssistantModelsResponse> {
  const token = getStoredAccessToken();
  const url = resolveApiUrl(`${ASSISTANT_BASE}/models`);
  const res = await fetch(url, {
    method: "GET",
    headers: {
      Accept: "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!res.ok) {
    throw new Error(await readErrorMessage(res));
  }
  const raw = (await res.json()) as Record<string, unknown>;
  const modelsRaw = raw.models;
  const models: AssistantModel[] = Array.isArray(modelsRaw)
    ? modelsRaw
        .map((item): AssistantModel | null => {
          const r = item as Record<string, unknown>;
          const id = typeof r.id === "string" ? r.id : "";
          if (!id) {
            return null;
          }
          return {
            id,
            label: typeof r.label === "string" ? r.label : id,
            backend: typeof r.backend === "string" ? r.backend : "",
            provider: typeof r.provider === "string" ? r.provider : undefined,
          };
        })
        .filter((m): m is AssistantModel => m !== null)
    : [];
  const defaultRaw = raw.default ?? raw.default_model ?? raw.defaultModel;
  const defaultId = typeof defaultRaw === "string" ? defaultRaw : "";
  return { models, default: defaultId };
}

type SseParseState = {
  buffer: string;
  eventName: string;
  assistantText: string;
  sawDone: boolean;
};

function createSseParseState(): SseParseState {
  return { buffer: "", eventName: "", assistantText: "", sawDone: false };
}

function consumeSseChunk(
  state: SseParseState,
  chunk: string,
  handlers: AssistantStreamHandlers
): void {
  state.buffer += chunk;
  const lines = state.buffer.split("\n");
  state.buffer = lines.pop() ?? "";

  for (const line of lines) {
    if (line.startsWith("event:")) {
      state.eventName = line.slice(6).trim();
      continue;
    }
    if (!line.startsWith("data:")) {
      continue;
    }
    const raw = line.slice(5).trim();
    if (!raw) {
      continue;
    }
    let data: Record<string, unknown>;
    try {
      data = JSON.parse(raw) as Record<string, unknown>;
    } catch {
      continue;
    }

    if (state.eventName === "tool") {
      handlers.onTool?.(data as AssistantStep);
      continue;
    }
    if (state.eventName === "done") {
      if (typeof data.text === "string") {
        state.assistantText = data.text;
      }
      state.sawDone = true;
      handlers.onDone?.(state.assistantText);
      continue;
    }
    if (state.eventName === "error" || data.error) {
      const msg =
        typeof data.error === "string"
          ? data.error
          : typeof data.message === "string"
            ? data.message
            : "Assistant error";
      handlers.onError?.(msg);
      continue;
    }
    // delta (and unnamed text chunks)
    if (typeof data.text === "string") {
      state.assistantText += data.text;
      handlers.onDelta?.(state.assistantText);
    }
  }
}

async function parseHttpErrorFromXhr(xhr: XMLHttpRequest): Promise<string> {
  const status = xhr.status || 0;
  const raw = (xhr.responseText || "").trim();
  if (!raw) {
    return `Request failed (${status})`;
  }
  try {
    const j = JSON.parse(raw) as Record<string, unknown>;
    const fromDetail = formatContractHubDetail(j.detail ?? j.message ?? j.error);
    if (fromDetail) {
      return fromDetail;
    }
  } catch {
    /* not JSON */
  }
  return raw.length > 300 ? `${raw.slice(0, 300)}…` : raw;
}

/**
 * Stream one assistant turn via XHR (progressive ``responseText``).
 */
export async function streamAssistantChat(args: {
  prompt: string;
  context?: AssistantChatContext | null;
  sessionId: string;
  model?: string | null;
  signal?: AbortSignal;
  handlers: AssistantStreamHandlers;
}): Promise<void> {
  const runOnce = (sessionId: string) =>
    new Promise<void>((resolve, reject) => {
      const token = getStoredAccessToken();
      const url = resolveApiUrl(`${ASSISTANT_BASE}/chat`);
      const xhr = new XMLHttpRequest();
      xhr.open("POST", url);
      xhr.setRequestHeader("Content-Type", "application/json");
      xhr.setRequestHeader("Accept", "text/event-stream");
      if (token) {
        xhr.setRequestHeader("Authorization", `Bearer ${token}`);
      }

      const state = createSseParseState();
      let processed = 0;

      const onAbort = () => {
        xhr.abort();
      };
      if (args.signal) {
        if (args.signal.aborted) {
          reject(new DOMException("Aborted", "AbortError"));
          return;
        }
        args.signal.addEventListener("abort", onAbort, { once: true });
      }

      const cleanup = () => {
        args.signal?.removeEventListener("abort", onAbort);
      };

      const ingest = () => {
        const full = xhr.responseText || "";
        if (full.length <= processed) {
          return;
        }
        const chunk = full.slice(processed);
        processed = full.length;
        consumeSseChunk(state, chunk, args.handlers);
      };

      xhr.onprogress = () => {
        ingest();
      };

      xhr.onerror = () => {
        cleanup();
        reject(new Error("Network error talking to Ask Cenna."));
      };

      xhr.onabort = () => {
        cleanup();
        reject(new DOMException("Aborted", "AbortError"));
      };

      xhr.onload = () => {
        cleanup();
        ingest();
        if (xhr.status === 404) {
          reject(Object.assign(new Error("Assistant session not found"), { status: 404 }));
          return;
        }
        if (xhr.status < 200 || xhr.status >= 300) {
          void parseHttpErrorFromXhr(xhr).then((msg) => reject(new Error(msg)));
          return;
        }
        if (!state.sawDone) {
          args.handlers.onDone?.(state.assistantText);
        }
        resolve();
      };

      xhr.send(
        JSON.stringify({
          prompt: args.prompt,
          context: args.context ?? null,
          session_id: sessionId,
          model: toAssistantModelId(args.model),
        })
      );
    });

  try {
    await runOnce(args.sessionId);
  } catch (err) {
    const status = (err as { status?: number })?.status;
    if (status !== 404) {
      throw err;
    }
    // Stale localStorage session — mint a new one and retry once.
    setStoredAssistantSessionId(null);
    const fresh = await createAssistantSession(args.context);
    await runOnce(fresh);
  }
}

/** Synthetic transcript ids for SSE turns (not retryable via add-in API). */
export function isAssistantSseMessageId(id: string): boolean {
  return id.startsWith("sse:");
}
